import type { ButtonHTMLAttributes, ReactNode, Ref } from "react";

import styles from "./Button.module.css";
import { LockIcon } from "./icons";

/**
 * Which knob to turn (decision Denis 24.10.2026, design 2026-10-24 §4.3,
 * §8.2 — it lifts UX gate round 1, #13):
 *
 * - `primary` — the main action of an area, in K60 red with a glint on hover.
 *   One per area: where two strong actions stand together, the second is
 *   `tinte`. A dialog's confirm is `primary`, even when it takes something
 *   back (red says „carry out" there).
 * - `signal` — the same red under the sign-in's old name; `tokens.test.ts`
 *   lists the files that may write it.
 * - `tinte` — the second strong action: the ink-filled button.
 * - `linie` — everything else: outlined, the default.
 * - `leise` — text-weight actions: underlined text, no box.
 *
 * `secondary` and `quiet` are the old names of `linie` and `leise` and look
 * exactly the same; they keep their own class names because several
 * screens' tests read them.
 */
type Variant = "signal" | "tinte" | "linie" | "leise" | "primary" | "secondary" | "quiet";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  /** `l` (48 px, body text) is for a single decisive action — the sign-in submit. */
  size?: "m" | "l";
  /** A leading glyph. Decorative: the label carries the name, the icon is `aria-hidden`. */
  icon?: ReactNode;
  /**
   * Work is under way. The button says so (`aria-busy`), stays in the tab
   * order, swallows the click, and keeps its own colours — it is *not*
   * `aria-disabled`, whose grey says „refused", and a working button is not a
   * refused one. A submit that must not fire twice is also passed `disabled`;
   * the busy state stays the visible part of that.
   */
  busy?: boolean;
  /**
   * A quiet button that starts a text line: its word stands on the edge of
   * the text above instead of 9 px in (screen check S-8). A negative margin
   * of exactly its inline padding and border — the hit area does not shrink.
   * Opt-in per use; the other screens keep their buttons as they are.
   */
  flush?: boolean;
  /** React 19 passes `ref` as a prop; `...props` hands it to the `<button>`. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * None of the five status colours appears on a control, so a colour on screen
 * describes data and never an action. The one colour a control may carry is
 * K60 red, on the main action of an area (`primary`, design 2026-10-24 §8.2):
 * a solid fill, where status red is a tinted fill with a word and a symbol.
 * See the note on --brand-red in tokens.css for where else red may stand.
 */
export function Button({
  variant = "secondary",
  size = "m",
  icon,
  busy = false,
  flush = false,
  className,
  onClick,
  "aria-disabled": ariaDisabled,
  children,
  ...props
}: ButtonProps) {
  // `aria-disabled` (unlike `disabled`) keeps the button reachable so its
  // reason is read out — but a reachable button that still fires its click
  // is not disabled at all. Fix-Runde 1, M1: the caller's `onClick` never
  // runs while the reason stands. A busy button swallows the click the same
  // way, without the grey.
  const isAriaDisabled = ariaDisabled === true || ariaDisabled === "true";
  const swallow = busy || isAriaDisabled;
  // A held main action is neutral, with a padlock before its word (Task 11,
  // UX-Gutachten 1, B-13): a paler red read as status red, and the button
  // looked broken instead of held on purpose. Decoration — `aria-hidden`, so
  // the name stays the word and the reason still describes it. Not while it
  // works: a working button is not a refused one.
  const red = variant === "primary" || variant === "signal";
  const locked = red && !busy && (isAriaDisabled || props.disabled === true);
  return (
    <button
      type="button"
      aria-disabled={ariaDisabled}
      aria-busy={busy ? true : undefined}
      className={`${styles.base} ${styles[variant]} ${size === "l" ? styles.l : ""} ${flush ? styles.flush : ""} ${className ?? ""}`}
      onClick={(event) => {
        if (swallow) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    >
      {locked ? <LockIcon size={14} /> : icon}
      {children}
    </button>
  );
}
